/**
 * TankEstimatesEditor — Settings → Reporting rules → "Tank level estimates" (Phase 13 / 13.1c,
 * DEC-351). GET / PATCH /v1/tenants/tank-estimates-settings (MANAGE_TENANT, the section's gate).
 *
 * ON (the default): the operator portal and the dispatcher Tanks view show levels estimated
 * from single-gauge tickets (top gauge × the tank's barrels per inch), always marked "est.".
 * OFF: they show recorded levels only. Saves immediately, like the other Settings toggles.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';

export const TANK_ESTIMATES_QUERY_KEY = ['tenants', 'tank-estimates-settings'] as const;
const URL = '/v1/tenants/tank-estimates-settings';

const help: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function TankEstimatesEditor(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useApiQuery<{ readonly enabled: boolean }>(apiOpts, URL, {
    queryKey: TANK_ESTIMATES_QUERY_KEY,
  });

  const mutation = useApiMutation<{ enabled: boolean }, { enabled: boolean }>(
    apiOpts,
    URL,
    'PATCH',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: TANK_ESTIMATES_QUERY_KEY });
        toast.success('Saved');
      },
      onError: () => {
        toast.error('Could not save the setting. Try again.');
      },
    },
  );

  if (isError) {
    return (
      <p style={{ color: 'var(--color-danger)', fontSize: '14px' }}>Failed to load this setting.</p>
    );
  }
  if (isLoading || data === undefined) {
    return <p style={{ color: 'var(--color-text-tertiary)', fontSize: '14px' }}>Loading…</p>;
  }

  return (
    <div data-testid="tank-estimates-editor" style={{ maxWidth: '640px' }}>
      <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={data.enabled}
          disabled={mutation.isPending}
          onChange={(e): void => mutation.mutate({ enabled: e.target.checked })}
        />
        <span>
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
            Show estimated tank levels and fill rates
          </span>
          <span style={{ ...help, display: 'block' }}>
            Most tickets record a gauge reading and barrels hauled, not the tank level. When this is
            on, lodeX Pro estimates each tank&apos;s level from the gauge and the tank&apos;s
            barrels per inch, and shows how fast it fills and when it will be full. Estimates are
            always marked &quot;est.&quot;. Turn it off to show only levels drivers recorded.
          </span>
          <span style={{ ...help, display: 'block', marginTop: '4px' }}>
            Applies to your customers&apos; portal and your Tanks view.
          </span>
        </span>
      </label>
    </div>
  );
}
